import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CarSearchPayload } from '@shared/models/car-search-payload.model';
import { CarSearchResponse, CarSearchResult } from '@shared/models/car-search-response.model';
import { DataLayerUtilsService } from '@shared/services/data-layer-utils.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { CAR_SEARCH_REFERENCE_ID, SELECTED_CAR, TRAVEL_CAR_SEARCH_LOCAL_STORAGE_KEY } from 'app.config';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import * as fromActions from '../actions/car-hire-search.actions';

@Injectable()
export class CarHireSearchEffects {
  private siteSettings$ = this.siteSettingsService.fetchSiteSettings().pipe(
    filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
    take(1)
  );

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private siteSettingsService: SiteSettingsService
  ) {}

  public restoreCar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.RESTORE_CAR_SEARCH),
      map(() => {
        const carSearchString = localStorage.getItem(TRAVEL_CAR_SEARCH_LOCAL_STORAGE_KEY);
        const carSelectedCarString = localStorage.getItem(SELECTED_CAR);
        const carSearchReferenceIdString = localStorage.getItem(CAR_SEARCH_REFERENCE_ID);

        const carSearch: CarSearchPayload | null = carSearchString ? JSON.parse(carSearchString) : null;
        if (carSearch) {
          const timezoneOffset = new Date().toISOString().substring(20);

          carSearch.start_date_full = `${carSearch.start_date_full.substring(0, 20)}${timezoneOffset}`;
          carSearch.end_date_full = `${carSearch.end_date_full.substring(0, 20)}${timezoneOffset}`;
        }

        const selectedCar: CarSearchResult | null = carSelectedCarString ? JSON.parse(carSelectedCarString) : null;
        const carSearchReferenceId: string | null = carSearchReferenceIdString || null;

        return new fromActions.RestoreCarSearchSuccess({
          carSearch,
          selectedCar,
          carSearchReferenceId,
        });
      })
    )
  );

  public createCarSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CREATE_CAR_SEARCH),
        map((action: fromActions.CreateCarSearch) => {
          this.saveToLocalStorage(action.payload);
        })
      ),
    { dispatch: false }
  );

  public getCarSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_CAR_SEARCH_RESULTS),
      switchMap((action: fromActions.GetCarSearchResults) => {
        // the api does not like the extra property but we need to have it in the action to add it to the store
        const payload = {
          ...action.payload,
        };
        delete payload.selected_start_location_name;
        delete payload.selected_end_location_name;
        return this.http.post('/api/search/cars/', payload).pipe(
          withLatestFrom(this.siteSettings$),
          takeUntil(this.actions$.pipe(ofType(fromActions.CHECK_CAR_SEARCH_CANCEL))),
          map(([data, settings]: [CarSearchResponse, ISiteSettings]) => {
            return new fromActions.GetCarSearchResultsSuccess(data, settings.switches.use_car_hire_payment_instalments);
          }),
          catchError((err) =>
            of(new fromActions.GetCarSearchResultsFailure(DataLayerUtilsService.extractValidationErrors(err)))
          )
        );
      })
    )
  );

  public clearCarSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_CAR_SEARCH),
        map(() => this.clearLocalStorage())
      ),
    { dispatch: false }
  );

  public saveToLocalStorage(data: CarSearchPayload) {
    localStorage.setItem(TRAVEL_CAR_SEARCH_LOCAL_STORAGE_KEY, JSON.stringify(data));
  }

  public clearLocalStorage() {
    localStorage.removeItem(TRAVEL_CAR_SEARCH_LOCAL_STORAGE_KEY);
    localStorage.removeItem(CAR_SEARCH_REFERENCE_ID);
    localStorage.removeItem(SELECTED_CAR);
  }
}
